import { useSystemConfig } from '@/lib/queries';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { SupportWidget } from './SupportWidget';

/**
 * Support.
 *
 * ── WHAT THIS PAGE IS NOW ───────────────────────────────────────────────────
 *
 * Two things and nothing else: the ways to reach ChiCare without a chat, and
 * a widget that opens one.
 *
 * It used to BE the chat — the conversation filled the screen. That is a
 * messaging app, and support is not one: somebody wants an answer while
 * carrying on with what they were doing, and a full-screen thread cannot be
 * dismissed and come back to.
 *
 * ── WHY THE OTHER WAYS STAY ON IT ───────────────────────────────────────────
 *
 * Live chat answers in minutes during working hours and not at all at 2am. The
 * phone number, WhatsApp and email are admin-owned settings and sit right
 * here, so somebody who needs an answer tonight is not left typing into a box
 * nobody is watching.
 */
export function SupportScreen() {
  const { data: config } = useSystemConfig();

  // Typed at the boundary the same way SiteFooter does it: the CMS block is
  // admin-shaped, and a strict type here would need changing every time
  // somebody adds a field in Admin › Settings.
  const contact = (config as { contact?: Record<string, string | undefined> } | undefined)
    ?.contact;

  const hours = contact?.hours?.trim();
  const email = contact?.support_email?.trim();
  const sellerEmail = contact?.seller_email?.trim();

  return (
    <>
      <AppBar back title="Support" />

      {/* pb-28: the launcher is fixed over this, and content ending under a
          button somebody cannot scroll past is content they never read. */}
      <div className="mx-auto w-full max-w-[720px] px-4 pb-28 pt-4 xl:px-6">
        <section
          data-testid="support-hero"
          className="relative overflow-hidden rounded-[16px] bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-5 py-5 text-white"
        >
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_88%_-10%,rgba(255,255,255,0.18),transparent_58%)]"
          />

          <div className="relative">
            <h1 className="m-0 font-display text-[21px] font-bold leading-[1.15] tracking-[-0.025em]">
              Talk to us
            </h1>
            <p className="m-0 mt-1.5 max-w-[44ch] text-[12.5px] leading-snug text-white/75">
              Orders, deliveries, a pack that looks wrong — a real person, not a form.
            </p>
            {hours ? (
              <p className="m-0 mt-2 font-mono text-[10px] uppercase tracking-[0.1em] text-white/60">
                {hours}
              </p>
            ) : null}
          </div>
        </section>

        <div data-testid="support-ways" className="mt-3 space-y-2">
          {email ? (
            <Way
              icon="doc"
              label="Email us"
              value={email}
              hint="Best for anything with a photo or a receipt"
              href={`mailto:${email}`}
            />
          ) : null}

          {/* Sellers have their own inbox. A payout question landing in the
              delivery queue is a day lost. */}
          {sellerEmail ? (
            <Way
              icon="store"
              label="Pharmacies and vet practices"
              value={sellerEmail}
              hint="Payouts, licences and listings"
              href={`mailto:${sellerEmail}`}
            />
          ) : null}

          {contact?.support_phone ? (
            <Way
              icon="phone"
              label="Call us"
              value={contact.support_phone}
              hint={hours}
              href={`tel:${contact.support_phone.replace(/\s/g, '')}`}
            />
          ) : null}

          {contact?.whatsapp ? (
            <Way
              icon="message"
              label="WhatsApp"
              value={contact.whatsapp}
              /* Nigerian numbers are given as 0803…; wa.me wants 234803…. */
              href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '').replace(/^0/, '234')}`}
            />
          ) : null}
        </div>

        <p className="mt-4 text-center text-[12px] leading-snug text-faint">
          Or open the chat. We ask for an email or a phone number first, so we can get back to you
          if the chat ends before your question does.
        </p>
      </div>

      {/* The widget lives here and only here. Pinned to every screen it would
          sit on top of whatever somebody was doing — the Add to cart button,
          on a phone — and hold a socket open across the whole shop. */}
      <SupportWidget />
    </>
  );
}

function Way({
  icon,
  label,
  value,
  hint,
  href,
}: {
  icon: 'phone' | 'message' | 'doc' | 'store';
  label: string;
  value: string;
  hint?: string;
  href: string;
}) {
  return (
    <a
      href={href}
      className="flex min-h-11 items-center gap-3 rounded-card border border-line bg-white px-4 py-3 no-underline"
    >
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-blue-tint">
        <Icon name={icon} size={16} strokeWidth={1.9} className="text-blue" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-bold text-ink">{label}</span>
        <span className="block truncate text-[12px] text-muted">{value}</span>
        {hint ? <span className="block truncate text-[11px] text-faint">{hint}</span> : null}
      </span>

      <Icon name="chevron" size={15} strokeWidth={2} className="shrink-0 text-faint" />
    </a>
  );
}
